import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FileSpreadsheetIcon,
  Loader2Icon,
  ShieldAlertIcon,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  account,
  data as dataApi,
  track,
  type DataMetric,
  type DataOverview,
  type WorkItem,
} from '@/lib/bridge';
import { describeError } from '@/lib/errorMessage';
import { useAccounts } from '@/lib/useAccounts';
import { useAppInfo } from '@/lib/useAppInfo';
import { useAppReady } from '@/lib/useAppReady';
import { AccountCard } from '@/components/AccountCard';
import { PageShell } from '@/components/PageShell';
import { MetricGridSkeleton, PageSkeleton, SkeletonCard } from '@/components/skeletons';
import { Card } from '@/components/ui/card';
import { CARD, MUTED } from '@/components/ui';

/** 百分比类指标（原生给的是 0~1 的小数）。 */
const RATIO_KEYS = new Set([
  'completion_rate_5s',
  'bounce_rate_2s',
  'cover_click_ratio',
  'interactive_rate',
]);

/** 总览里默认展示的指标（顺序即卡片顺序）。 */
const SHOW_KEYS = [
  'play_cnt',
  'digg_cnt',
  'comment_cnt',
  'share_count',
  'publish_cnt',
  'completion_rate_5s',
  'bounce_rate_2s',
  'cover_click_ratio',
  'avg_view_second',
  'homepage_view_cnt',
  'total_fans_cnt',
  'net_fans_cnt',
];

type SortKey = 'play' | 'digg' | 'comment' | 'collect';
type DeepRow = {
  title: string;
  publishedAt: number;
  completionRate?: number;
  bounceRate2s?: number;
  coverClickRate?: number;
  avgViewSecond?: number;
  homepageView?: number;
  fansGrowth?: number;
};

function formatValue(key: string, value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  if (RATIO_KEYS.has(key)) return `${(n * 100).toFixed(1)}%`;
  if (key === 'avg_view_second') return `${n.toFixed(2)}s`;
  if (Math.abs(n) < 1000) return `${n}`;
  return n.toLocaleString('zh-CN');
}

/** `20260919` → `09-19` */
function formatDateKey(key: string): string {
  if (key.length !== 8) return key;
  return `${key.slice(4, 6)}-${key.slice(6, 8)}`;
}

/** 毫秒时间戳 → `18:32`（缓存更新时间用） */
function formatClock(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 标题归一化：Excel 里的标题可能被截断，用前 12 字比对。 */
function normalizeTitle(text: string): string {
  return (text || '').trim().replace(/\s+/g, '').slice(0, 12);
}

function toNumber(value: unknown): number | undefined {
  if (value == null) return undefined;
  if (typeof value === 'number') return value;
  const raw = String(value).trim();
  if (!raw || raw === '-') return undefined;
  if (raw.endsWith('%')) {
    const parsed = Number.parseFloat(raw.slice(0, -1));
    return Number.isNaN(parsed) ? undefined : parsed / 100;
  }
  const parsed = Number.parseFloat(raw);
  return Number.isNaN(parsed) ? undefined : parsed;
}

type CacheEntry<T> = { at: number; value: T };

const CACHE_PREFIX = 'bxy.data.v1.';

/** 读本地缓存；解析失败一律当没有。 */
function readCache<T>(key: string): CacheEntry<T> | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry<T>;
    return parsed && typeof parsed.at === 'number' ? parsed : null;
  } catch {
    return null;
  }
}

/** 写本地缓存；写不下就放弃（下次还是能拉）。 */
function writeCache(key: string, value: unknown): number {
  const at = Date.now();
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ at, value }));
  } catch {
    /* ignore */
  }
  return at;
}

export function DataPage() {
  const { loading: infoLoading, isNative } = useAppInfo();
  const {
    accounts,
    selectedId,
    setSelectedId,
    error: accountError,
    loading: accountsLoading,
  } = useAccounts();

  const [overview, setOverview] = useState<DataOverview | null>(null);
  const [metricKey, setMetricKey] = useState<string>('play_cnt');
  const [recentDays, setRecentDays] = useState<7 | 30>(7);
  const [works, setWorks] = useState<WorkItem[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>('play');
  const [deep, setDeep] = useState<Record<string, DeepRow>>({});
  const [deepCount, setDeepCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingWorks, setLoadingWorks] = useState(false);
  const [aggregate, setAggregate] = useState<
    { play: number; fans: number; works: number } | null
  >(null);
  const [importing, setImporting] = useState(false);
  const [cachedAt, setCachedAt] = useState<number | null>(null);
  /** 手动刷新用的触发器；变化会让下面两个 effect 重跑 */
  const [refreshTick, setRefreshTick] = useState(0);

  const loaded = !infoLoading && !accountsLoading;
  useAppReady(loaded);

  useEffect(() => {
    track.pageOpen('data');
  }, []);

  const currentAccount = accounts.find((a) => a.id === selectedId) ?? null;
  const writable = useMemo(() => accounts.filter((a) => a.canWrite), [accounts]);

  // 汇总：把所有可写账号的近 7 天总览加起来
  const loadAggregate = useCallback(async (list: typeof accounts) => {
    const results = await Promise.allSettled(
      list
        .filter((a) => a.canWrite)
        .map((a) => dataApi.overview(a.id, 7)),
    );
    const sum = (key: string) =>
      results.reduce((total, r) => {
        if (r.status !== 'fulfilled') return total;
        const metric = r.value.metrics.find((m) => m.key === key);
        return total + (Number(metric?.value) || 0);
      }, 0);
    if (results.every((r) => r.status === 'rejected')) return;
    setAggregate({
      play: sum('play_cnt'),
      fans: sum('total_fans_cnt'),
      works: sum('publish_cnt'),
    });
  }, []);

  // 选中账号的总览：先用缓存把数渲染出来（进页面不转圈），再后台刷新
  useEffect(() => {
    if (!currentAccount?.canWrite) {
      setOverview(null);
      return;
    }
    let cancelled = false;
    const key = `overview.${currentAccount.id}.${recentDays}`;
    const cached = readCache<DataOverview>(key);
    if (cached) {
      setOverview(cached.value);
      setCachedAt(cached.at);
      setLoadingOverview(false);
    } else {
      setLoadingOverview(true);
    }
    setError(null);
    dataApi
      .overview(currentAccount.id, recentDays)
      .then((next) => {
        if (cancelled) return;
        setOverview(next);
        setCachedAt(writeCache(key, next));
        setError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          cached
            ? `刷新失败：${describeError(e)}（下面是缓存数据）`
            : describeError(e),
        );
      })
      .finally(() => {
        if (!cancelled) setLoadingOverview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentAccount?.id, recentDays, refreshTick]);

  // 选中账号的作品列表：同样先渲染缓存
  useEffect(() => {
    if (!currentAccount?.canWrite) {
      setWorks([]);
      return;
    }
    let cancelled = false;
    const key = `works.${currentAccount.id}`;
    const cached = readCache<WorkItem[]>(key);
    if (cached) {
      setWorks(cached.value);
      setCachedAt(cached.at);
      setLoadingWorks(false);
    } else {
      setLoadingWorks(true);
    }
    dataApi
      .works(currentAccount.id, { count: 50 })
      .then((res) => {
        if (cancelled) return;
        setWorks(res.items);
        setCachedAt(writeCache(key, res.items));
        setError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(cached ? `刷新失败：${describeError(e)}（下面是缓存数据）` : describeError(e));
      })
      .finally(() => {
        if (!cancelled) setLoadingWorks(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentAccount?.id, refreshTick]);

  // 只在首次有账号时算一次汇总
  useEffect(() => {
    if (writable.length > 0 && aggregate === null) {
      void loadAggregate(accounts);
    }
  }, [writable.length, aggregate, accounts, loadAggregate]);

  const switchAccount = useCallback(async () => {
    try {
      setSelectedId((await account.pick('authenticated')).id);
    } catch {
      // 用户取消：保留当前账号
    }
  }, [setSelectedId]);

  const metrics = overview?.metrics ?? [];
  const visibleMetrics = useMemo(
    () => SHOW_KEYS.map((key) => metrics.find((m) => m.key === key)).filter(
      (m): m is DataMetric => Boolean(m),
    ),
    [metrics],
  );
  const activeMetric = visibleMetrics.find((m) => m.key === metricKey) ?? visibleMetrics[0];
  const maxTrend = Math.max(
    ...(activeMetric?.trends ?? []).map((t) => Number(t.value) || 0),
    1,
  );

  const sortedWorks = useMemo(() => {
    const score = (w: WorkItem) => Number(w.statistics[sortKey] ?? -1);
    return [...works].sort((a, b) => score(b) - score(a));
  }, [works, sortKey]);

  const importExcel = useCallback(async (file: File) => {
    setImporting(true);
    setError(null);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
        defval: null,
      });
      if (!rows.length || rows[0]['作品名称'] == null) {
        throw new Error('不是「作品列表导出」格式的文件（缺少「作品名称」列）');
      }
      const table: Record<string, DeepRow> = {};
      for (const row of rows) {
        const title = String(row['作品名称'] ?? '').trim();
        if (!title) continue;
        const publishedAt = Date.parse(String(row['发布时间'] ?? ''));
        table[`${normalizeTitle(title)}|${Number.isNaN(publishedAt) ? '' : publishedAt}`] =
          {
            title,
            publishedAt: Number.isNaN(publishedAt) ? 0 : publishedAt,
            completionRate: toNumber(row['完播率']),
            bounceRate2s: toNumber(row['2s跳出率']),
            coverClickRate: toNumber(row['封面点击率']),
            avgViewSecond: toNumber(row['平均播放时长']),
            homepageView: toNumber(row['主页访问量']),
            fansGrowth: toNumber(row['粉丝增量']),
          };
      }
      setDeep(table);
      setDeepCount(Object.keys(table).length);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setImporting(false);
    }
  }, []);

  // Excel 导入的行优先；没有就用接口自带的 metrics（两者等价，接口的免上传）
  const deepOf = useCallback(
    (w: WorkItem): DeepRow | undefined => {
      const imported =
        deep[`${normalizeTitle(w.desc)}|${w.createdAt ?? ''}`] ??
        deep[normalizeTitle(w.desc)];
      if (imported) return imported;
      const m = w.metrics;
      if (!m) return undefined;
      const fansGrowth = (m.subscribe_count ?? 0) - (m.unsubscribe_count ?? 0);
      return {
        title: w.desc,
        publishedAt: w.createdAt ?? 0,
        completionRate: m.completion_rate ?? undefined,
        bounceRate2s: m.bounce_rate_2s ?? undefined,
        coverClickRate: m.cover_click_rate ?? undefined,
        avgViewSecond: m.avg_view_second ?? undefined,
        homepageView: m.homepage_visit_count ?? undefined,
        fansGrowth: fansGrowth || undefined,
      };
    },
    [deep],
  );

  const canLoad = loaded && currentAccount?.canWrite === true;

  return (
    <PageShell
      title="数据分析"
      description="各账号播放/互动趋势与单篇笔记表现"
      backTo="/"
    >
      {currentAccount ? (
        <AccountCard
          account={currentAccount}
          label="分析账号"
          detail={writable.length > 1 ? `${writable.length} 个账号可切换` : undefined}
          onClick={writable.length > 1 ? () => void switchAccount() : undefined}
        />
      ) : null}

      {!isNative && loaded ? (
        <Card className={CARD}>
          <p className="text-muted-foreground text-xs">
            当前不在 App 内。浏览器里单独调试请加 <code>?mock=1</code>。
          </p>
        </Card>
      ) : null}

      {loaded && !currentAccount ? (
        <Card className={CARD}>
          <div className="flex items-center gap-2">
            <ShieldAlertIcon className="text-primary size-5 shrink-0" />
            <strong className="text-sm font-semibold">没有可分析的账号</strong>
          </div>
          <p className="text-muted-foreground mt-1 text-xs">
            需要至少一个已登录账号，请先在 App 内登录后再来。
          </p>
        </Card>
      ) : null}

      {/* 全账号汇总 */}
      {aggregate ? (
        <Card className={CARD}>
          <p className={MUTED}>全部账号合计 · 近7天</p>
          <div className="mt-2 grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-title2 font-semibold tabular-nums">
                {formatValue('play_cnt', aggregate.play)}
              </div>
              <div className="text-caption1 text-muted-foreground">总播放</div>
            </div>
            <div>
              <div className="text-title2 font-semibold tabular-nums">
                {formatValue('total_fans_cnt', aggregate.fans)}
              </div>
              <div className="text-caption1 text-muted-foreground">总粉丝</div>
            </div>
            <div>
              <div className="text-title2 font-semibold tabular-nums">
                {formatValue('publish_cnt', aggregate.works)}
              </div>
              <div className="text-caption1 text-muted-foreground">投稿量</div>
            </div>
          </div>
        </Card>
      ) : null}

      {/* 选中账号的指标 */}
      {canLoad ? (
        <Card className={CARD}>
          <div className="flex items-center justify-between gap-2">
            <p className={MUTED}>
              {loadingOverview
                ? '正在读取数据…'
                : `当前账号 · 近${recentDays}天${
                    cachedAt ? ` · 更新于 ${formatClock(cachedAt)}` : ''
                  }`}
            </p>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setRefreshTick((n) => n + 1)}
                className="text-muted-foreground rounded-full px-2 py-0.5 text-xs active:bg-accent"
              >
                {loadingOverview || loadingWorks ? '刷新中…' : '刷新'}
              </button>
              {([7, 30] as const).map((days) => (
                <button
                  key={days}
                  type="button"
                  onClick={() => setRecentDays(days)}
                  className={
                    recentDays === days
                      ? 'bg-foreground/10 text-foreground rounded-full px-2.5 py-0.5 text-xs'
                      : 'text-muted-foreground rounded-full px-2.5 py-0.5 text-xs'
                  }
                >
                  近{days}天
                </button>
              ))}
            </div>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-2">
            {loadingOverview && visibleMetrics.length === 0 ? (
              <MetricGridSkeleton count={6} />
            ) : (
              visibleMetrics.map((metric) => (
                <button
                  key={metric.key}
                  type="button"
                  onClick={() => setMetricKey(metric.key)}
                  className={
                    'rounded-control bg-muted/60 px-2 py-2.5 text-left transition-colors ' +
                    (metric.key === activeMetric?.key
                      ? 'ring-1 ring-primary'
                      : 'active:bg-accent')
                  }
                >
                  <div className="text-caption1 text-muted-foreground truncate">
                    {metric.name}
                  </div>
                  <div className="text-subheadline font-semibold tabular-nums truncate">
                    {formatValue(metric.key, metric.value)}
                  </div>
                </button>
              ))
            )}
          </div>

          {/* 选中指标的日趋势 */}
          {activeMetric?.trends.length ? (
            <div className="mt-3">
              <p className={MUTED}>{activeMetric.name} · 每日趋势</p>
              <div className="mt-2 flex h-28 gap-1">
                {activeMetric.trends.map((trend) => {
                  const value = Number(trend.value) || 0;
                  // 用确定像素高度：百分比高度相对 auto 父级会塌缩成 0
                  const barPx = Math.round((value / maxTrend) * 64) + 4;
                  return (
                    <div
                      key={trend.date}
                      className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1"
                    >
                      <span className="text-caption1 text-muted-foreground tabular-nums">
                        {formatValue(activeMetric.key, value)}
                      </span>
                      <div
                        className="bg-foreground/70 w-full rounded-t"
                        style={{ height: `${barPx}px` }}
                      />
                      <span className="text-caption1 text-muted-foreground">
                        {formatDateKey(trend.date)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </Card>
      ) : null}

      {/* 单帖榜 */}
      {canLoad ? (
        <Card className={CARD}>
          <div className="flex items-center justify-between gap-2">
            <p className={MUTED}>
              {loadingWorks ? '正在读取作品…' : `作品 ${sortedWorks.length} 篇`}
            </p>
            <div className="flex gap-1">
              {(
                [
                  ['play', '播放'],
                  ['digg', '点赞'],
                  ['comment', '评论'],
                  ['collect', '收藏'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSortKey(key)}
                  className={
                    sortKey === key
                      ? 'bg-foreground/10 text-foreground rounded-full px-2.5 py-0.5 text-xs'
                      : 'text-muted-foreground rounded-full px-2.5 py-0.5 text-xs'
                  }
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {sortedWorks.length === 0 && !loadingWorks ? (
            <p className="text-muted-foreground text-xs leading-snug">
              这个账号暂时读不到作品数据，请确认登录有效。
            </p>
          ) : null}

          <div className="mt-2 flex flex-col gap-2">
            {sortedWorks.map((work) => {
              const extra = deepOf(work);
              return (
                <div key={work.awemeId} className="bg-muted/60 rounded-control px-3 py-2.5">
                  <div className="flex min-w-0 items-center gap-3">
                    {work.cover ? (
                      <img
                        src={work.cover}
                        alt=""
                        loading="lazy"
                        className="size-9 shrink-0 rounded object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : null}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-subheadline">
                        {work.desc || '无作品描述'}
                      </div>
                      <div className="text-caption1 text-muted-foreground tabular-nums">
                        播放 {formatValue('play_cnt', work.statistics.play)} ·{' '}
                        点赞 {formatValue('digg_cnt', work.statistics.digg)} ·{' '}
                        评论 {formatValue('comment_cnt', work.statistics.comment)} ·{' '}
                        收藏 {formatValue('comment_cnt', work.statistics.collect)}
                      </div>
                      {extra ? (
                        <div className="text-caption1 text-primary tabular-nums">
                          5s完播 {formatValue('completion_rate_5s', extra.completionRate)} ·
                          2s跳出 {formatValue('bounce_rate_2s', extra.bounceRate2s)} ·
                          主页访问 {formatValue('homepage_view_cnt', extra.homepageView)}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {loadingWorks && sortedWorks.length === 0 ? (
            <SkeletonCard rows={3} title={false} />
          ) : null}
        </Card>
      ) : null}

      {/* Excel 导入（接口已给深度指标，这里只是补齐用的补充手段） */}
      {canLoad ? (
        <Card className={CARD}>
          <p className={MUTED}>导入 Excel 补充</p>
          <p className="text-muted-foreground mt-1 text-xs leading-snug">
            完播率 / 2s 跳出率 / 封面点击 / 平均播放时长 / 主页访问 / 粉丝增减
            都已经直接从创作者中心接口取到，<b>不需要上传文件</b>。
            个别作品缺列时，可以用「内容管理 → 导出数据」的{' '}
            <b>作品列表导出.xlsx</b> 补齐（按标题+发布时间匹配）。
            {deepCount > 0 ? ` 已导入 ${deepCount} 篇。` : ''}
          </p>
          <label className="mt-2 block">
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void importExcel(file);
                e.target.value = '';
              }}
            />
            <span className="bg-foreground/10 text-foreground inline-flex w-full items-center justify-center gap-2 rounded-control px-4 py-2.5 text-sm font-medium">
              {importing ? (
                <Loader2Icon className="size-4 animate-spin" />
              ) : (
                <FileSpreadsheetIcon className="size-4" />
              )}
              {importing ? '解析中…' : deepCount > 0 ? '重新导入 Excel' : '选择 Excel 文件'}
            </span>
          </label>
        </Card>
      ) : null}

      {error ?? accountError ? (
        <p className="text-destructive px-1 text-sm">{error ?? accountError}</p>
      ) : null}

      {!loaded ? <PageSkeleton kind="data" /> : null}
    </PageShell>
  );
}
